import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Paper,
  Tabs,
  Tab,
  Chip,
  Divider
} from '@mui/material';
import TimeSeriesChart from '../TimeSeriesChart/TimeSeriesChart';
import HierarchySelector from './HierarchySelector';
import HierarchyBreadcrumbs from './HierarchyBreadcrumbs';
import LevelNavigation from './LevelNavigation';
import { forecastRunsService } from '../../services/forecastruns.service';
import { forecastHierarchyService } from '../../services/forecastHierarchy.service';

const ForecastCanvas = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [topLineData, setTopLineData] = useState(null);
  const [runDetails, setRunDetails] = useState(null);
  const [hierarchies, setHierarchies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedHierarchy, setSelectedHierarchy] = useState(null);
  
  // Navigation state
  const [navigationPath, setNavigationPath] = useState([]);
  const [currentNode, setCurrentNode] = useState(null);
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  const [activeTab, setActiveTab] = useState(0);



  // Fetch top-line data and hierarchies
  useEffect(() => {
    const fetchData = async () => {
      if (!runId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch run details first
        const runDetailsResponse = await forecastRunsService.getForecastRunById(runId);
        if (runDetailsResponse.status === 'success') {
          setRunDetails(runDetailsResponse.data);
          console.log('Run details fetched:', runDetailsResponse.data);
        } else {
          console.warn('Failed to fetch run details:', runDetailsResponse.message);
        }

        // Fetch top-line forecast values
        const topLineResponse = await forecastHierarchyService.getTopLineValues(runId);
        if (topLineResponse.status === 'success') {
          setTopLineData(topLineResponse);
        } else {
          throw new Error(topLineResponse.message || 'Failed to fetch top-line data');
        }

        // Fetch available hierarchies
        const hierarchiesResponse = await forecastHierarchyService.getHierarchies();
        if (hierarchiesResponse.status === 'success') {
          setHierarchies(hierarchiesResponse.data.hierarchies || []);
        } else {
          console.warn('Failed to fetch hierarchies:', hierarchiesResponse.message);
        }

      } catch (error) {
        console.error('Error fetching forecast data:', error);
        setError(error.message || 'Failed to load forecast data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [runId]);

  // Get forecast start date from run details
  const getForecastStartDate = () => {
    if (runDetails && runDetails.run_timestamp) {
      const runForecastStart = new Date(runDetails.run_timestamp);
      return new Date(runForecastStart.getFullYear(), runForecastStart.getMonth(), runForecastStart.getDate());
    }
    return null;
  };

  // Prepare time series data for the chart
  const getTimeSeriesData = () => {
    if (!topLineData || !topLineData.time_series_data) {
      return [];
    }

    // Add a dummy group ID for top-line data (since it's aggregated)
    return topLineData.time_series_data.map(point => ({
      ...point,
      group_id: 'top-line' // Add group ID for consistency
    }));
  };

  const handleHierarchySelect = (hierarchy) => {
    console.log('handleHierarchySelect called with:', hierarchy);
    setSelectedHierarchy(hierarchy);
    setNavigationPath([]);
    setCurrentNode(null);
    
    // Fetch hierarchy levels
    if (hierarchy) {
      console.log('Fetching levels for hierarchy:', hierarchy.hierarchy_id);
      fetchHierarchyLevels(hierarchy.hierarchy_id);
    }
  };

  const fetchHierarchyLevels = async (hierarchyId) => {
    try {
      console.log('Fetching hierarchy levels for hierarchy ID:', hierarchyId);
      const response = await forecastHierarchyService.getHierarchyLevels(hierarchyId);
      console.log('Hierarchy levels API response:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Is response.data an array?', Array.isArray(response.data));
      console.log('Response.data.levels:', response.data?.levels);
      console.log('Response.data.levels type:', typeof response.data?.levels);
      console.log('Is response.data.levels an array?', Array.isArray(response.data?.levels));
      
      if (response.status === 'success') {
        // Check if levels are nested under data.levels instead of data directly
        let levels = [];
        if (Array.isArray(response.data)) {
          levels = response.data;
        } else if (Array.isArray(response.data?.levels)) {
          levels = response.data.levels;
        } else {
          console.warn('No levels found in response:', response);
          levels = [];
        }
        
        console.log('Final levels array:', levels);
        console.log('Levels array length:', levels.length);
        if (levels.length > 0) {
          console.log('First level:', levels[0]);
        }
        setHierarchyLevels(levels);
      } else {
        console.error('Failed to fetch hierarchy levels:', response.message);
        setHierarchyLevels([]);
      }
    } catch (error) {
      console.error('Error fetching hierarchy levels:', error);
      setHierarchyLevels([]);
    }
  };

  const handleNavigate = (type, id, name) => {
    if (type === 'hierarchy') {
      setNavigationPath([]);
      setCurrentNode(null);
    } else if (type === 'level') {
      setNavigationPath([{ type: 'level', id, name }]);
      setCurrentNode(null);
    } else if (type === 'node') {
      setCurrentNode({ id, name });
    }
  };

  const handleNavigateToNode = (node) => {
    // Navigate to standalone node details page with forecast start date
    const forecastStartDate = getForecastStartDate();
    const queryParams = forecastStartDate ? `?forecastStartDate=${forecastStartDate.toISOString().split('T')[0]}` : '';
    navigate(`/forecast-hierarchy/${runId}/hierarchy/${selectedHierarchy.hierarchy_id}/node/${node.node_id}${queryParams}`);
  };

  const handleNavigateToChild = (childNode) => {
    setCurrentNode(childNode);
    setNavigationPath(prev => [...prev, { type: 'node', id: childNode.node_id, name: childNode.node_name }]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading forecast data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Top-Line Chart Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Top-Line Forecast"
          subheader={`Run ID: ${runId}`}
        />
        <CardContent>
          <TimeSeriesChart
            timeSeriesData={getTimeSeriesData()}
            forecastStartDate={getForecastStartDate()}
            title={`Top-Line Forecast - Run: ${runId}`}
            subtitle={runDetails?.algorithm_name || 'Forecast Algorithm'}
            groupingConfig={{
              groupBy: 'group_id',
              seriesTypes: ['aggregated', 'external']
            }}
            seriesConfig={{
              aggregated: {
                mean: 'organic_p50',
                lower: 'organic_p10',
                upper: 'organic_p90',
                label: 'Aggregated'
              },
              external: {
                mean: 'external_p50',
                lower: 'external_p10',
                upper: 'external_p90',
                label: 'External'
              }
            }}
            initialWindowWeeks={8}
            showControls={true}
            showLegend={true}
            height="500px"
          />
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      {topLineData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {topLineData.total_periods || topLineData.time_series_data?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Periods
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {topLineData.coverage?.active_nodes || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Nodes
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {topLineData.coverage?.active_hierarchies || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Hierarchies
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {topLineData.run_metadata?.status || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Run Status
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Hierarchy Selection - Now below the chart */}
      <HierarchySelector 
        onHierarchySelect={handleHierarchySelect}
        selectedHierarchyId={selectedHierarchy?.hierarchy_id}
      />

      {/* Breadcrumb Navigation */}
      {selectedHierarchy && (
        <HierarchyBreadcrumbs
          runId={runId}
          hierarchyId={selectedHierarchy.hierarchy_id}
          hierarchyName={selectedHierarchy.hierarchy_name}
          navigationPath={navigationPath}
          onNavigate={handleNavigate}
        />
      )}

      {/* Navigation Tabs */}
      {selectedHierarchy && (
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Top-Line View" />
            <Tab label="Level Navigation" />
          </Tabs>
        </Box>
      )}

      {/* Tab Content */}
      {activeTab === 1 && selectedHierarchy && (
        <>
          {console.log('Rendering LevelNavigation with props:', {
            runId,
            hierarchyId: selectedHierarchy.hierarchy_id,
            hierarchyName: selectedHierarchy.hierarchy_name,
            levels: hierarchyLevels,
            levelsLength: hierarchyLevels.length
          })}
          <LevelNavigation
            runId={runId}
            hierarchyId={selectedHierarchy.hierarchy_id}
            hierarchyName={selectedHierarchy.hierarchy_name}
            levels={hierarchyLevels}
            onNavigateToNode={handleNavigateToNode}
            onNavigateToLevel={(level) => handleNavigate('level', level.level_id, level.display_name || level.level_name)}
          />
        </>
      )}

      {/* Summary Statistics */}
      {topLineData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {topLineData.total_periods || topLineData.time_series_data?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Periods
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {topLineData.coverage?.active_nodes || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Nodes
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {topLineData.coverage?.active_hierarchies || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Hierarchies
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {topLineData.run_metadata?.status || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Run Status
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Available Hierarchies Section */}
      <Card>
        <CardHeader
          title="Available Hierarchies"
          subheader="Select a hierarchy to drill down into detailed forecasts"
        />
        <CardContent>
          {hierarchies.length === 0 ? (
            <Typography color="text.secondary">
              No hierarchies available for this forecast run.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {hierarchies.map((hierarchy) => (
                <Grid item xs={12} md={6} lg={4} key={hierarchy.hierarchy_id}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      cursor: 'pointer',
                      border: selectedHierarchy?.hierarchy_id === hierarchy.hierarchy_id 
                        ? '2px solid #1976d2' 
                        : '1px solid #e0e0e0',
                      '&:hover': {
                        borderColor: '#1976d2',
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                    onClick={() => handleHierarchySelect(hierarchy)}
                  >
                    <Typography variant="h6" gutterBottom>
                      {hierarchy.hierarchy_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type: {hierarchy.hierarchy_type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Levels: {hierarchy.levels?.length || 0}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={hierarchy.hierarchy_type} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                      {hierarchy.version && (
                        <Chip 
                          label={`v${hierarchy.version}`} 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Selected Hierarchy Details */}
      {selectedHierarchy && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title={`${selectedHierarchy.hierarchy_name} - Details`}
            subheader={`Hierarchy ID: ${selectedHierarchy.hierarchy_id}`}
          />
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Hierarchy Levels
            </Typography>
            <Box sx={{ mt: 2 }}>
              {selectedHierarchy.levels?.map((level, index) => (
                <Box key={level.level_id} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">
                    {index + 1}. {level.display_name} ({level.level_name})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Source: {level.source_table}.{level.source_column}
                    {level.is_leaf_level && (
                      <Chip label="Leaf Level" size="small" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  {index < selectedHierarchy.levels.length - 1 && (
                    <Divider sx={{ mt: 1 }} />
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          💡 The top-line chart shows aggregated forecast values across all hierarchies. 
          Select a hierarchy above to drill down into detailed node-level forecasts.
        </Typography>
      </Box>
    </Box>
  );
};

export default ForecastCanvas; 